import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  CandidateMessageItem,
  CandidateMessageThread,
  CandidateMessageThreadSummary,
  agencyGetCandidateThread,
  agencyListCandidateThreads,
  agencyReplyCandidateThread,
} from '../../api/api'
import styles from './CandidateMessagesInbox.module.css'

const bubbleClassFor = (sender: CandidateMessageItem['sender_type']) => {
  switch (sender) {
    case 'candidate':
      return styles.bubbleCandidate
    case 'agency':
      return styles.bubbleAgency
    case 'bot':
      return styles.bubbleBot
    default:
      return ''
  }
}

const formatTime = (iso: string): string => {
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleString()
  } catch {
    return ''
  }
}

const CandidateMessagesInbox: React.FC = () => {
  const [threads, setThreads] = useState<CandidateMessageThreadSummary[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [thread, setThread] = useState<CandidateMessageThread | null>(null)
  const [loading, setLoading] = useState(true)
  const [threadLoading, setThreadLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const scrollerRef = useRef<HTMLDivElement>(null)

  const loadThreads = useCallback(async () => {
    const res = await agencyListCandidateThreads()
    if ('error' in res) {
      setError(res.error)
      setLoading(false)
      return
    }
    setThreads(res.threads)
    setLoading(false)
    // Auto-select the first one if nothing selected
    if (res.threads.length > 0 && selectedId == null) {
      setSelectedId(res.threads[0].candidateid)
    }
  }, [selectedId])

  const loadThread = useCallback(async (id: number) => {
    setThreadLoading(true)
    setError(null)
    const res = await agencyGetCandidateThread(id)
    setThreadLoading(false)
    if ('error' in res) {
      setError(res.error)
      setThread(null)
      return
    }
    setThread(res)
    setTimeout(() => {
      if (scrollerRef.current) {
        scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight
      }
    }, 50)
  }, [])

  useEffect(() => {
    loadThreads()
    // Refresh the thread list every 20s so new messages from other
    // candidates bubble to the top without a manual reload.
    const id = window.setInterval(loadThreads, 20000)
    return () => window.clearInterval(id)
  }, [loadThreads])

  useEffect(() => {
    if (selectedId == null) return
    loadThread(selectedId)
    // Poll the currently open thread every 15s so agency staff see
    // candidate replies (and bot suggestions) live without reopening.
    const id = window.setInterval(() => loadThread(selectedId), 15000)
    return () => window.clearInterval(id)
  }, [selectedId, loadThread])

  const send = async () => {
    if (!selectedId) return
    const body = reply.trim()
    if (!body || sending) return
    setSending(true)
    const res = await agencyReplyCandidateThread(selectedId, body)
    setSending(false)
    if ('error' in res) {
      setError(res.error)
      return
    }
    setReply('')
    // Refresh thread view with new messages
    if (thread) {
      setThread({ ...thread, messages: res.messages })
    }
    // Move this thread to top / refresh list
    loadThreads()
    setTimeout(() => {
      if (scrollerRef.current) {
        scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight
      }
    }, 50)
  }

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      send()
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageTitle}>Candidate messages</div>
      {error && <div className={styles.errorBox}>{error}</div>}

      <div className={styles.split}>
        <div className={styles.threadList}>
          {loading && <div className={styles.muted}>Loading…</div>}
          {!loading && threads.length === 0 && (
            <div className={styles.muted}>No candidate messages yet.</div>
          )}
          {threads.map(t => {
            const isActive = t.candidateid === selectedId
            return (
              <button
                key={t.candidateid}
                className={`${styles.threadItem} ${isActive ? styles.active : ''}`}
                onClick={() => setSelectedId(t.candidateid)}
              >
                <div className={styles.threadHeader}>
                  <span className={styles.threadName}>{t.candidate_name}</span>
                  {t.unread_count > 0 && (
                    <span className={styles.unreadPill}>{t.unread_count}</span>
                  )}
                </div>
                <div className={styles.threadPreview}>{t.last_message_preview}</div>
                <div className={styles.threadTime}>{formatTime(t.last_message_at)}</div>
              </button>
            )
          })}
        </div>

        <div className={styles.threadPanel}>
          {!selectedId && <div className={styles.muted}>Select a thread to view messages.</div>}
          {selectedId != null && (
            <>
              <div className={styles.threadPanelHeader}>
                <div>
                  <strong>{thread?.candidate_name || '…'}</strong>
                  {thread?.candidate_email && (
                    <div className={styles.muted}>{thread.candidate_email}</div>
                  )}
                </div>
              </div>

              <div className={styles.messages} ref={scrollerRef}>
                {threadLoading && <div className={styles.muted}>Loading messages…</div>}
                {!threadLoading && thread?.messages.length === 0 && (
                  <div className={styles.muted}>No messages in this thread yet.</div>
                )}
                {thread?.messages.map(m => (
                  <div key={m.messageid}>
                    <div
                      className={styles.senderLabel}
                      style={{ textAlign: m.sender_type === 'agency' ? 'right' : 'left' }}
                    >
                      {m.sender_name} · {formatTime(m.created_at)}
                    </div>
                    <div className={`${styles.bubble} ${bubbleClassFor(m.sender_type)}`}>
                      {m.body}
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.replyRow}>
                <textarea
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Reply to candidate… (Ctrl/Cmd+Enter to send)"
                  disabled={sending}
                />
                <button onClick={send} disabled={sending || !reply.trim()}>
                  {sending ? 'Sending…' : 'Send reply'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default CandidateMessagesInbox
